import { connect } from 'pwa-helpers';
import { html } from 'lit';
import store from '../../store/store';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseComponent } from '../../features/base-component/base-component';
import { ProductSmallCardData } from '../product-card/product-card-display';
import { StoreState } from '../../store/store.interface';
import { getCart } from './shop-cart/store/shop-cart.selectors';
import { MINICART_SIZE } from '../../common/constants/shop-api';
import { booleanConverter } from '../../common/converters';
import { ShopCart } from './shop-cart/interfaces/shop-cart.interface';
import { getImageSrcUrl } from '../../common/utils/product-util';
import '../product-card/product-card-display';
import { getShopCart } from './shop-cart/thunks/get-shop-cart.thunk';
import { getFormatText, getProductLink } from './cart-list/utils/cart-list-util';
import { OfferTypeChecker } from '../../common/utils/offer-type-util';
import {
  announceViaAccessibleName,
  focusCartCardWithAnnouncement,
  resolveCartRemoval,
} from './utils/cart-preview-a11y-util';

/**
 * An element to show products in your cart.
 *
 * @slot empty - If the empty slot is used, the emptyMessage property is ignored.
 *
 * @csspart empty - Each product entry in the cart preview. This is only used if the empty slot is not used.
 * @csspart entry - The container of the cart preview.
 */
@customElement('mhecomm-cart-preview')
export class CartPreview extends connect(store)(BaseComponent) {
  /**
   * The message to display when the cart is empty. If the empty slot is provided, this property is ignored.
   */
  @property()
  emptyMessage = 'Your cart is empty';

  /**
   * Element to focus when the last item has been removed from cart preview.
   */
  @property()
  focusOnEmpty = '#cart_msg';

  /**
   * Whether or not the empty slot should be used. If false, the emptyMessage will be used instead of the empty slot.
   */
  @property({ converter: booleanConverter })
  useEmptySlot = false;

  /**
   * Which store to show a cart for. If not set it will show the national cart.
   */
  @property()
  store?: string;

  @state()
  private products: Array<ProductSmallCardData> = [];

  /**
   * The index of the product that was removed from the cart.
   */
  private pendingFocusIndex: number | null = null;

  /**
   * The message to announce when a product is removed from the cart.
   */
  private pendingRemoveMessage = '';

  connectedCallback() {
    super.connectedCallback();
    store.dispatch(getShopCart({ store: this.store }));
    this.addTrackedEventListener(this, 'removed-from-cart', this.onRemovedFromCart.bind(this));
  }

  /**
   * Handles focus and announcement whenever an item is removed from the cart preview.
   * The removal message is attached to the accessible name of the element focus
   * moves to, so it's read as part of the focus announcement (no aria-live race).
   *
   * @param event
   */
  private onRemovedFromCart(event: Event): void {
    const { removedIndex, message } = resolveCartRemoval(event, this.products);
    this.pendingFocusIndex = removedIndex;
    this.pendingRemoveMessage = message;
  }

  stateChanged(state: StoreState): void {
    const previousLength = this.products.length;
    const allProducts = this.getProducts(getCart(state)).sort((a, b) => b.modifiedDate - a.modifiedDate);
    this.products = allProducts.length > MINICART_SIZE ? allProducts.slice(0, MINICART_SIZE) : allProducts;

    if (this.pendingFocusIndex !== null && this.products.length < previousLength) {
      const index = this.pendingFocusIndex;
      const message = this.pendingRemoveMessage;
      this.pendingFocusIndex = null;
      this.pendingRemoveMessage = '';
      this.updateComplete.then(() => this.moveFocusAfterRemoval(index, message));
    }
  }

  /**
   * @param removedIndex
   * @param message
   */
  private moveFocusAfterRemoval(removedIndex: number, message: string): void {
    focusCartCardWithAnnouncement(this.renderRoot, removedIndex, this.products.length, message, (message: string): void => {
      const emptyElement = this.renderRoot.querySelector<HTMLElement>('[part="empty"]');
      if (emptyElement) {
        announceViaAccessibleName(emptyElement, message);
        return;
      }

      // Fallback: focus an external element specified by the host, or notify the parent.
      const target = this.focusOnEmpty ? document.querySelector<HTMLElement>(this.focusOnEmpty) : null;
      if (target) {
        target.setAttribute('tabindex', '-1');
        announceViaAccessibleName(target, message);
      } else {
        this.dispatchEvent(
          new CustomEvent('cart-preview-emptied', { bubbles: true, composed: true, detail: { message: message } })
        );
      }
    });
  }

  renderComp() {
    return html`
      <div>
        ${this.products.length === 0
          ? html`
              ${this.useEmptySlot
                ? html` <slot name="empty"></slot> `
                : html` <p part="empty" tabindex="-1">${this.emptyMessage}</p> `}
            `
          : ''}
        ${this.products.length > 0
          ? html`
              <ul class="list-unstyled">
                ${this.products.map(
                  (product) => {
                    const createCustomCartPreview = OfferTypeChecker.isCreateCustom(product?.offerType);
                    return html`
                      <li>
                        <mhecomm-product-card-display part="entry" .product=${product} ?isCalledFromCartPreview=${createCustomCartPreview}> </mhecomm-product-card-display>
                      </li>
                    `;
                  }
                )}
              </ul>
            `
          : ''}
      </div>
    `;
  }

  /**
   * The products being displayed.
   */
  getProducts(cart?: ShopCart): Array<ProductSmallCardData> {
    return (
      (cart !== undefined &&
        cart?.products?.map((product) => (
        {
          kind: 'ProductSmallCardData',
          title: product.productMetadata.title,
          image: getImageSrcUrl(product.productMetadata.imageUrl),
          url: getProductLink(product, this.store),
          createdDate: product.createdDate,
          modifiedDate: product.modifiedDate,
          format: getFormatText(product),
          formatTypeDesc: product.productMetadata.formatTypeDesc,
          businessUnit: product.productIdentifier.businessUnit,
          isbn: product.productIdentifier.isbn10,
          isbn13: product.productIdentifier.isbn13,
          programSourceId: product.productIdentifier.programSourceId,
          mhid: product.productIdentifier.mhid,
          offerPricingId: product.productIdentifier.offerPricingId,
          offerType: product.productMetadata.offerType,
          durationType: product.productMetadata.durationType,
          isEvergreen: product.productMetadata?.isEvergreen,
          edition: product.productMetadata?.edition,
          editionOrRelease: product.productMetadata?.editionOrRelease,
          isVirtual: product.productMetadata?.virtual,
          duration: product.productMetadata?.duration,
          durationPeriod: product.productMetadata?.durationPeriod,
          createCoverImages: product.createCustomProductMetadata?.createCoverImages,
        }))) ||
      []
    );
  }

  /**
   * Forces the component to update the cart. This is useful if the cart was updated outside of ecomm-ui and then client code needs to tell ecomm-ui cart to update itself.
   */
  updateCart() {
    store.dispatch(getShopCart({ store: this.store }));
  }

  mergeCart() {
    store.dispatch(getShopCart({}));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-cart-preview': CartPreview;
  }
}
